import { createSelector } from 'reselect';
import { fromJS } from 'immutable';
import * as K from '../constants/constants';
import isWithinBounds from '../helpers/isWithinBounds';
import parseObjectTypeName from '../helpers/parseObjectTypeName';
import { fetchObjectData } from '../api/objectData';

const getCurrentObjectCategory = (store) =>
  store.viewstate.get('currentSearchCategory');
const getObject = (store, props) =>
  store[store.viewstate.get('currentSearchCategory')] || fromJS({});
const getCity = (store, props) =>
  props.match.params.city === K.URI_ALL_CITIES ? '' : props.match.params.city;
const getObjectType = (store, props) =>
  props.match.params.objectType === K.URI_ALL_OBJECTTYPES
    ? ''
    : props.match.params.objectType;
const getSearchLimit = (store, props) => props.searchLimit;
const getKey = (store, keyStr) => keyStr;
const getBounds = (store) => store.viewstate.get('mapBounds');
const getSelectedAreaFilter = (store, props) =>
  store.viewstate.get('selectedAreaFilter');
const getSelectedRentFilter = (store, props) =>
  store.viewstate.get('selectedRentFilter');
const getIsMobileDevice = (store) => store.viewstate.get('isMobileDevice');

const getRentFilters = (store) => store.rentFilters;
const getCategoriesWithRentFilters = () => [K.RESIDENCE];

// Collect city parent/child relationships from backend
let parentCityList = fromJS({});

fetchObjectData({
  url:
    process.env.NODE_ENV === 'development'
      ? K.URL_CITY_RELATIONSHIP_DEV
      : K.URL_CITY_RELATIONSHIP,
}).then((data) => {
  parentCityList = fromJS(data);
});

export const getRentFilterForCategory = createSelector(
  [getRentFilters, getCurrentObjectCategory, getCategoriesWithRentFilters],
  (rentFilter, currentObjectCategory, categories) => {
    return categories.indexOf(currentObjectCategory) > -1
      ? rentFilter
      : fromJS({});
  }
);
// Get objects based on location
export const getObjectByCity = createSelector(
  [getObject, getCity],
  (objects, city) => {
    if (!city) {
      return objects;
    }

    return objects.filter((item) => {
      return item.get('city')
        ? item.get('city').toLocaleLowerCase() === city
        : false;
    });
  }
);

// Get objects based first on location and then filter on objectType
export const getObjectByObjectType = createSelector(
  [getObjectByCity, getObjectType],
  (objects, objectType) => {
    if (!objectType) {
      return objects;
    }

    return objects.filter((item) => {
      return item.get('objectType')
        ? item.get('objectType').toLocaleLowerCase() === objectType
        : false;
    });
  }
);

export const getObjectByArea = createSelector(
  [getObjectByObjectType, getSelectedAreaFilter],
  (objects, selectedFilter) => {
    if (!selectedFilter) {
      return objects;
    }

    return objects.filter((obj) => {
      const isAboveMin = obj.get('areaTotal') >= selectedFilter.get('min');
      const isAboveMax = obj.get('areaTotal') <= selectedFilter.get('max');
      return isAboveMin && isAboveMax;
    });
  }
);

export const getObjectByRent = createSelector(
  [getObjectByArea, getSelectedRentFilter],
  (objects, getSelectedRentFilter) => {
    if (!getSelectedRentFilter) {
      return objects;
    }

    return objects.filter((obj) => {
      return (
        (obj.get('rent') >= getSelectedRentFilter.get('min') &&
          obj.get('rent') <= getSelectedRentFilter.get('max')) ||
        getSelectedRentFilter.get('path') === 'alla_hyresnivaer'
      );
    });
  }
);

export const getObjectWithLimit = createSelector(
  [getSearchLimit, getObjectByObjectType],
  (limit, objects) => {
    if (!limit || limit === 0 || objects.size < limit) {
      return objects;
    }
    return objects.take(limit);
  }
);

export const getObjectFiltered = createSelector(
  [getObjectByRent],
  (objects) => {
    return objects;
  }
);

export const getObjectsWithinBounds = createSelector(
  [getObjectFiltered, getBounds],
  (filteredObjects, bounds) => {
    if (!bounds) {
      return filteredObjects;
    }

    // return objects within bounds, and objects that are missing geolocation data
    return filteredObjects.filter(
      (obj) =>
        !obj.getIn(['geolocation', 'latitude']) ||
        !obj.getIn(['geolocation', 'longitude']) ||
        isWithinBounds(obj, bounds)
    );
  }
);

// Collect an object based on a key.
export const getParamsByKey = createSelector(
  [getObject, getKey],
  (data, keyStr) => {
    let type;
    let types = {};

    try {
      if (!data) {
        return null;
      }

      data.map((item) => {
        type = item.get(keyStr);
        if (!type) {
          return null;
        }

        type = type.toLocaleLowerCase();

        return (types[type] = {
          amount: (types[type] && types[type].amount + 1) || 1,
          name: type,
          label: parseObjectTypeName(type),
          parent:
            parentCityList &&
            parentCityList.findKey((item) => item.includes(type)),
        });
      });

      return fromJS(types);
    } catch (e) {
      throw e;
    }
  }
);



// WEBPACK FOOTER //
// ./src/js/selectors/objects.js